import { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface TextMessageInputProps {
  onSendMessage: (text: string) => void;
  isConnected: boolean;
  isListening?: boolean;
  brandColor?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const TextMessageInput = ({
  onSendMessage,
  isConnected,
  isListening = false,
  brandColor = "#4E9CFF",
  placeholder = "Type a message...",
  disabled = false,
}: TextMessageInputProps) => {
  const [text, setText] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    if (text.trim() && isConnected && !disabled) {
      onSendMessage(text);
      setText("");
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Pulse animation when listening
  useEffect(() => {
    if (isListening && inputRef.current) {
      inputRef.current.style.animation = "none";
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.animation = "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite";
        }
      }, 10);
    }
  }, [isListening]);

  return (
    <div
      className="w-full transition-all duration-200"
      style={{
        opacity: isConnected ? 1 : 0.5,
        pointerEvents: isConnected ? "auto" : "none",
      }}
    >
      <div
        className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 bg-white ${
          isFocused
            ? "border-opacity-100"
            : "border-opacity-40"
        }`}
        style={{
          borderColor: isFocused ? brandColor : "#E5E7EB",
        }}
      >
        <Input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled || !isConnected}
          className="border-0 focus-visible:ring-0 focus-visible:outline-none p-0 h-auto text-sm"
        />
        <Button
          onClick={handleSend}
          disabled={!text.trim() || !isConnected || disabled}
          size="sm"
          className="flex-shrink-0 h-8 w-8 p-0 rounded-md transition-all"
          style={{
            backgroundColor: !text.trim() || !isConnected || disabled ? "#E5E7EB" : brandColor,
            color: "white",
          }}
          onMouseEnter={(e) => {
            if (text.trim() && isConnected && !disabled) {
              e.currentTarget.style.opacity = "0.9";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          <Send size={16} />
        </Button>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};
